/**
 * LegacyBookPreviewPage — web preview for one person's Legacy Book (PDF-1).
 * Route: /person/:personId/legacy-book
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getApiBase } from '../utils/apiBase';
import { humanizeError } from '../utils/errorHandling';
import { personHref } from '../utils/prettyArchiveLinks';
import {
  loadLegacyBookPreviewOptions,
  saveLegacyBookPreviewOptions,
} from '../utils/legacyBookPreview';
import { LegacyBookPreviewToolbar } from '../components/legacy-book/LegacyBookPreviewToolbar';
import { LegacyBookPreviewContent } from '../components/legacy-book/LegacyBookPreviewContent';
import { LoadingState } from '../components/ui/LoadingState';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import '../components/legacy-book/legacy-book-preview.css';

const API = getApiBase();

export default function LegacyBookPreviewPage() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState(null);
  const [tree, setTree] = useState(null);
  const [relationships, setRelationships] = useState({
    parents: [],
    children: [],
    partnerships: [],
    siblings: [],
  });
  const [stories, setStories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [memorial, setMemorial] = useState(null);
  const [options, setOptions] = useState(() => loadLegacyBookPreviewOptions(personId));

  const handleOptionsChange = useCallback(
    (next) => {
      setOptions(next);
      saveLegacyBookPreviewOptions(person?.id || personId, next);
    },
    [person?.id, personId],
  );

  useEffect(() => {
    if (!personId) return;
    setOptions(loadLegacyBookPreviewOptions(personId));
  }, [personId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const personRes = await axios.get(`${API}/individuals/detail/${personId}`);
        const loaded = personRes.data;
        const pid = loaded.id;

        if (cancelled) return;

        setPerson(loaded);
        setRelationships({
          parents: loaded.parents || [],
          children: loaded.children || [],
          partnerships: loaded.partnerships || [],
          siblings: loaded.siblings || [],
        });

        const [docsRes, storiesRes, treeRes, memorialRes] = await Promise.allSettled([
          axios.get(`${API}/documents/${pid}`),
          axios.get(`${API}/individuals/${pid}/stories`),
          axios.get(`${API}/trees/${loaded.tree_id}`),
          axios.get(`${API}/memorials-api/by-person/${pid}`),
        ]);

        if (cancelled) return;

        setDocuments(docsRes.status === 'fulfilled' && Array.isArray(docsRes.value.data) ? docsRes.value.data : []);
        setStories(
          storiesRes.status === 'fulfilled' && Array.isArray(storiesRes.value.data) ? storiesRes.value.data : [],
        );
        setTree(treeRes.status === 'fulfilled' ? treeRes.value.data : null);
        setMemorial(
          memorialRes.status === 'fulfilled' ? memorialRes.value.data?.memorial || null : null,
        );
      } catch (error) {
        if (!cancelled) {
          toast.error(humanizeError(error));
          navigate('/dashboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [personId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5efe6]">
        <LoadingState message="Preparing Legacy Book preview…" />
      </div>
    );
  }

  if (!person) return null;

  const backLink = personHref(person);

  return (
    <div
      className="legacy-book-preview-root"
      data-theme={options.theme}
      data-testid="legacy-book-preview-page"
    >
      <header className="legacy-book-no-print sticky top-0 z-20 border-b border-[#e8e2d8]/80 bg-[#fffdf8]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={backLink}
              className="inline-flex items-center gap-1.5 text-xs text-[#6c6258] hover:text-[#2c2824]"
              data-testid="legacy-book-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Profile
            </Link>
            <span className="hidden text-[#e8e2d8] sm:inline" aria-hidden>
              |
            </span>
            <div className="hidden min-w-0 sm:block">
              <BrandWordmark className="text-sm" />
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#5c4a32]">
            <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate font-serif text-sm">Legacy Book preview</span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="legacy-book-no-print mb-6 max-w-2xl">
          <h1 className="font-serif text-2xl font-light text-[#1c1917] sm:text-3xl">Preview Legacy Book</h1>
          <p className="mt-2 text-sm leading-relaxed text-[#6c6258]">
            See how this profile could become a keepsake Legacy Book. Export-ready design coming next — your existing
            archive PDF export remains available from profile settings.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,17rem)_1fr] lg:items-start">
          <LegacyBookPreviewToolbar
            options={options}
            onChange={handleOptionsChange}
            className="lg:sticky lg:top-20"
          />

          <div className="min-w-0 pb-12">
            <LegacyBookPreviewContent
              person={person}
              tree={tree}
              relationships={relationships}
              stories={stories}
              documents={documents}
              memorial={memorial}
              options={options}
            />
          </div>
        </div>
      </div>

      {!user ? null : (
        <p className="legacy-book-no-print sr-only" aria-live="polite">
          Legacy Book preview loaded for {person.given_names} {person.surname}
        </p>
      )}
    </div>
  );
}
